import {
  Action,
  AIONZ_DIALOG_DATA,
  AionzDialog,
  AionzDialogRef,
  CardTableModule,
  ConfirmationModalService,
  ConfirmResult,
  ModalTypes,
  UserAvatarComponent
} from '@aionzdev/design-system';
import { DatePipe, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatChip } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  AcceptPurchaseRequestDialogData,
  AcceptPurchaseRequestDialogResult
} from '../../../interfaces/accept-purchase-request-dialog-data';
import { PurchaseItemStatus, PurchaseRequest } from '../../../interfaces/purchase-request';
import { PurchaseRequestStatusComponent } from '../../atoms/purchase-request-status/purchase-request-status.component';
import { GenericPurchaseRequestDialogComponent } from '../generic-purchase-request-dialog/generic-purchase-request-dialog.component';

interface DeclineReasonParams {
  headerTitle: string;
  title: string;
  description: string;
  labelConfirm: string;
  labelCancel: string;
  justificationPlaceholder: string;
}

@Component({
  selector: 'aionz-accept-purchase-request-dialog',
  imports: [
    MatChip,
    PurchaseRequestStatusComponent,
    DatePipe,
    CardTableModule,
    MatTableModule,
    UserAvatarComponent,
    NgOptimizedImage,
    GenericPurchaseRequestDialogComponent,
    MatMenuModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './accept-purchase-request-dialog.component.html',
  styleUrl: './accept-purchase-request-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AcceptPurchaseRequestDialogComponent implements OnInit {
  private _dialogService = inject(ConfirmationModalService);
  public data: AcceptPurchaseRequestDialogData = inject(AIONZ_DIALOG_DATA);
  public dialog = inject(AionzDialog);
  public dialogRef = inject(AionzDialogRef<AcceptPurchaseRequestDialogComponent>);
  private _destroyRef = inject(DestroyRef);
  protected displayedColumns: string[] = [
    'requestedAt',
    'fileName',
    'requester',
    'status',
    'actions'
  ];

  protected purchaseRequests = signal<PurchaseRequest[]>([]);
  protected dataSource = computed(() => new MatTableDataSource(this.purchaseRequests()));
  protected declineReasons = signal<Record<string, string>>({});

  protected hasModifiedItems = computed(() => {
    const currentRequests = this.purchaseRequests();
    const originalRequests = this.data.requests;

    return currentRequests.some(current => {
      const original = originalRequests.find(orig => orig.id === current.id);
      return original && original.status !== current.status;
    });
  });

  private readonly _defaultReason = 'Recusou sem motivos';

  ngOnInit(): void {
    this.purchaseRequests.set(this.data.requests);
    this.declineReasons.set({});

    this.dialogRef.disableClose = true;

    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.cancel());

    this.dialogRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event: KeyboardEvent) => {
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.preventDefault();
          event.stopPropagation();
          this.cancel();
        }
      });
  }

  protected downloadFile(purchaseId: string) {
    const purchaseRequest = this.purchaseRequests().find(request => request.id === purchaseId);
    if (purchaseRequest?.file?.url) {
      const link = document.createElement('a');
      link.href = purchaseRequest.file.url;
      link.download = purchaseRequest.file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  protected cancel() {
    if (this.hasModifiedItems()) {
      this.showDiscardChangesModal();
    } else {
      this.dialogRef.close();
    }
  }

  private showDiscardChangesModal() {
    this._dialogService
      .open(
        {
          headerTitle: 'Descartar Alterações',
          title: 'Deseja descartar as alterações?',
          description:
            'Você fez alterações nesta solicitação que ainda não foram salvas. Se você fechar este modal agora, todas as modificações serão perdidas.',
          labelConfirm: 'Continuar Editando',
          labelCancel: 'Descartar e Fechar'
        },
        ModalTypes.DANGER
      )
      .subscribe(res => {
        if (res !== Action.CONFIRM) {
          this.dialogRef.close();
        }
      });
  }

  protected decline() {
    this.openDeclineReasonDialog({
      headerTitle: 'Recusar solicitação',
      title: 'Motivo da recusa',
      description: '',
      labelConfirm: 'Desejo recusar e enviar motivo da recusa',
      labelCancel: 'Recusar sem motivos',
      justificationPlaceholder: 'Digite aqui o motivo pelo qual o pedido foi recusado.'
    }).subscribe(result => {
      if (result.action === Action.CLOSED) {
        return;
      }

      if (result.action === Action.CANCEL) {
        this.confirmDeclineWithoutReason().subscribe(confirmed => {
          if (confirmed) {
            this.dialogRef.close({ approve: false, reason: '' });
          }
        });
        return;
      }

      this.dialogRef.close({ approve: false, reason: result.justification || '' });
    });
  }

  protected approve() {
    this._dialogService
      .open(
        {
          headerTitle: 'Aprovar Solicitação',
          title: 'Deseja aprovar essa solicitação de compra?',
          description:
            'Após a confirmação, caso haja itens aceitos, será possível enviar o pedido de cotação aos fornecedores.',
          labelConfirm: 'Confirmar e publicar',
          labelCancel: 'Ainda não',
          icon: undefined
        },
        ModalTypes.INFO
      )
      .subscribe(res => {
        if (res === Action.CONFIRM) {
          const currentReasons = this.declineReasons();
          const result: AcceptPurchaseRequestDialogResult[] = this.purchaseRequests().map(
            item => ({
              status: item.status,
              id: item.id,
              reason:
                item.status === PurchaseItemStatus.DECLINED ? currentReasons[item.id] : undefined
            })
          );
          this.dialogRef.close(result);
        }
      });
  }

  protected approveItem(item: PurchaseRequest) {
    this._dialogService
      .open(
        {
          headerTitle: 'Confirmar Aprovação',
          title: 'Aprovar arquivo?',
          description: `Você tem certeza que deseja  aprovar o ${item.file.name}?`,
          labelConfirm: 'Aprovar',
          labelCancel: 'Cancelar'
        },
        ModalTypes.INFO
      )
      .subscribe(res => {
        if (res === Action.CONFIRM) {
          this.purchaseRequests.update(requests =>
            requests.map(req =>
              req.id === item.id ? { ...req, status: PurchaseItemStatus.APPROVED } : req
            )
          );
          this.declineReasons.update(reasons => {
            const { [item.id]: removed, ...rest } = reasons;
            return rest;
          });
        }
      });
  }

  protected declineItem(item: PurchaseRequest) {
    this.openDeclineReasonDialog({
      headerTitle: 'Recusar solicitação',
      title: 'Motivo da recusa',
      description: `Digite o motivo da recusa para o arquivo ${item.file.name}.`,
      labelConfirm: 'Desejo recusar e enviar motivo da recusa',
      labelCancel: 'Recusar sem motivos',
      justificationPlaceholder: 'Digite aqui o motivo pelo qual o pedido foi recusado.'
    }).subscribe(result => {
      if (result.action === Action.CLOSED) {
        return;
      }

      if (result.action === Action.CANCEL) {
        this.confirmDeclineWithoutReason().subscribe(confirmed => {
          if (confirmed) {
            this.purchaseRequests.update(requests =>
              requests.map(req =>
                req.id === item.id ? { ...req, status: PurchaseItemStatus.DECLINED } : req
              )
            );
            this.declineReasons.update(reasons => ({
              ...reasons,
              [item.id]: this._defaultReason
            }));
          }
        });
        return;
      }

      this.purchaseRequests.update(requests =>
        requests.map(req =>
          req.id === item.id ? { ...req, status: PurchaseItemStatus.DECLINED } : req
        )
      );
      this.declineReasons.update(reasons => ({
        ...reasons,
        [item.id]: result.justification || this._defaultReason
      }));
    });
  }

  protected changeDeclineReason(item: PurchaseRequest) {
    const currentReason = this.declineReasons()[item.id] || '';

    this._dialogService
      .openWithJustification(
        {
          headerTitle: 'Alterar motivo da recusa',
          title: 'Atualize o motivo da recusa',
          description: `Altere o motivo da recusa para o arquivo ${item.file.name}.`,
          labelConfirm: 'Salvar motivo',
          labelCancel: 'Cancelar'
        },
        {
          justificationPlaceholder: 'Digite o novo motivo da recusa',
          justificationRequired: true
        },
        ModalTypes.DANGER
      )
      .subscribe((result: ConfirmResult) => {
        if (result.action !== Action.CONFIRM) {
          return;
        }

        this.declineReasons.update(reasons => ({
          ...reasons,
          [item.id]: result.justification || currentReason || this._defaultReason
        }));
      });
  }

  private openDeclineReasonDialog(texts: DeclineReasonParams) {
    return this._dialogService.openWithJustification(
      {
        headerTitle: texts.headerTitle,
        title: texts.title,
        description: texts.description,
        labelConfirm: texts.labelConfirm,
        labelCancel: texts.labelCancel
      },
      {
        justificationPlaceholder: texts.justificationPlaceholder,
        justificationRequired: false
      },
      ModalTypes.DANGER
    );
  }

  private confirmDeclineWithoutReason() {
    return this._dialogService
      .open(
        {
          headerTitle: 'Recusar solicitação',
          title: 'Deseja mesmo recusar essa solicitação?',
          description: 'Após essa ação o usuário irá receber a notificação de que a solicitação dele foi recusada.',
          labelConfirm: 'Desejo recusar',
          labelCancel: 'Cancelar'
        },
        ModalTypes.DANGER
      )
      .pipe(map((action: Action) => action === Action.CONFIRM));
  }

  protected getStatusClass(status: PurchaseItemStatus): string {
    const statusClassMap: Record<string, string> = {
      [PurchaseItemStatus.PENDING]: 'pending',
      [PurchaseItemStatus.APPROVED]: 'approved',
      [PurchaseItemStatus.DECLINED]: 'declined'
    };

    return statusClassMap[status] || 'unknown';
  }

  get approvalPending() {
    return (
      this.purchaseRequests().length === 0 ||
      this.purchaseRequests().some(request => request.status === PurchaseItemStatus.PENDING)
    );
  }
}
